import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";

const inputCls =
  "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

export default function Login() {
  const { login, reactivate } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [rcode, setRcode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    if (reactivating) {
      const res = await reactivate({ email, password, bottle_code: rcode });
      setLoading(false);
      if (res.ok) {
        setReactivating(false);
        setRcode("");
        setNotice(res.message || "Account reactivated. Please sign in.");
      } else {
        setError(res.error);
      }
      return;
    }
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      if (res.user.role === "installer" && res.user.status !== "active") {
        navigate("/pending-approval");
      } else {
        navigate("/dashboard");
      }
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">SGBINOXE Care</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white">Welcome back</h1>
      <p className="mt-2 text-sm text-neutral-500">Sign in to your installer workspace.</p>

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
        {error && (
          <div className="rounded-lg border border-[#E30613]/30 bg-[#E30613]/10 px-4 py-3 text-sm text-[#FF6069]" data-testid="login-error">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-400" data-testid="login-notice">
            {notice}
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-neutral-300">Business Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@business.com"
            required
            className={inputCls}
            data-testid="login-email"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-neutral-300">Password</Label>
            <Link to="/forgot-password" className="text-xs text-neutral-500 transition-colors hover:text-[#FF1C2A]" data-testid="forgot-link">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className={inputCls}
              data-testid="login-password"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white" aria-label="Toggle password">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {reactivating && (
          <div className="space-y-2">
            <Label className="text-neutral-300">Bottle Activation Code</Label>
            <Input value={rcode} onChange={(e) => setRcode(e.target.value)} required placeholder="01A-0001-K9Q4X" className={`${inputCls} font-mono tracking-wide`} data-testid="reactivate-code" />
            <p className="text-xs text-neutral-500">Scan or enter a new unused SGBINOXE bottle code to reactivate your suspended account.</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="flex h-11 w-full items-center justify-center rounded-lg bg-[#E30613] font-medium text-white transition-colors duration-200 hover:bg-[#FF1C2A] disabled:opacity-60"
          data-testid="login-submit"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : reactivating ? "Reactivate Account" : "Sign In"}
        </button>

        <button
          type="button"
          onClick={() => { setReactivating((v) => !v); setError(""); setNotice(""); }}
          className="w-full text-center text-xs text-neutral-500 transition-colors hover:text-[#FF1C2A]"
          data-testid="toggle-reactivate"
        >
          {reactivating ? "← Back to sign in" : "Account suspended? Reactivate with a new bottle code"}
        </button>
      </form>

      <p className="mt-8 text-sm text-neutral-500">
        Don't have an account?{" "}
        <Link to="/register" className="font-medium text-white transition-colors hover:text-[#FF1C2A]" data-testid="register-link">
          Register as an installer
        </Link>
      </p>
    </AuthShell>
  );
}
